import { useState, useEffect } from 'react';
import { ChevronRight, Phone, Mail, Menu, X } from 'lucide-react';
import AboutSection from './components/AboutSection';
import TestimonialsSection from './components/TestimonialsSection';
import GallerySection from './components/GallerySection';
import WhatsAppButton from './components/WhatsAppButton';

const SBJGlassworks = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setMobileMenuOpen(false);
    }
  };

  const products = [
    {
      category: 'Window Blinds',
      items: [
        { name: 'Zebra Blinds', desc: 'Elegant dual-layer design for perfect light control' },
        { name: 'Roller Blinds', desc: 'Sleek and minimalist window treatment' },
        { name: 'Wooden Blinds for Kitchen', desc: 'Natural warmth meets functionality' },
        { name: 'Vertical Blinds', desc: 'Ideal for large windows and sliding doors' },
        { name: 'Roman Blind', desc: 'Timeless elegance with soft fabric folds' },
        { name: 'Customized Blinds for Mandir', desc: 'Sacred spaces deserve special care' }
      ],
      gradient: 'from-amber-600 via-amber-500 to-yellow-400'
    },
    {
      category: 'Wallpaper',
      items: [
        { name: 'Chinese WP', desc: '21 Inch width - Traditional artistry' },
        { name: 'Korean WP', desc: '42 Inch width - Contemporary designs' },
        { name: 'Customized Wallpaper', desc: 'Your vision, our craftsmanship' },
        { name: 'Self Adhesive WP', desc: 'Easy application, stunning results' }
      ],
      gradient: 'from-purple-600 via-pink-500 to-rose-400'
    },
    {
      category: 'Glass Films',
      items: [
        { name: 'Frosted Glass Film', desc: 'Privacy with a touch of elegance' },
        { name: 'Decorative Glass Film', desc: 'Transform ordinary glass into art' },
        { name: 'Golden Glass Film', desc: 'Luxurious metallic finish' },
        { name: 'Sun-Controlled Glass Film', desc: 'Comfort and energy efficiency' },
        { name: 'Wooden Design Vinyl', desc: 'Natural aesthetics without maintenance' },
        { name: 'Customized Glass Film', desc: 'Unique designs for unique spaces' }
      ],
      gradient: 'from-blue-600 via-cyan-500 to-teal-400'
    },
    {
      category: 'Others',
      items: [
        { name: 'Grass Sheet', desc: 'Bring nature indoors' },
        { name: 'UV Sheet', desc: 'Durable protection with style' },
        { name: 'Self Adhesive Wallpaper', desc: 'Quick transformation solution' },
        { name: 'Louvers', desc: 'Ventilation meets sophistication' },
        { name: 'PVC Sheet', desc: 'Versatile and long-lasting' },
        { name: 'Vertical Garden', desc: 'Living walls for modern spaces' }
      ],
      gradient: 'from-emerald-600 via-green-500 to-lime-400'
    }
  ];

  const filteredProducts = activeCategory === 'all'
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Skip to Content Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-6 focus:py-3 focus:bg-amber-500 focus:text-slate-900 focus:rounded-lg focus:font-semibold"
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'bg-slate-900/95 backdrop-blur-lg shadow-2xl' : 'bg-transparent'}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-slate-900">SBJ</span>
              </div>
              <div>
                <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 bg-clip-text text-transparent">
                  SBJ Glassworks
                </div>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" onClick={(e) => scrollToSection(e, 'home')} className="hover:text-amber-400 transition-colors">Home</a>
              <a href="#products" onClick={(e) => scrollToSection(e, 'products')} className="hover:text-amber-400 transition-colors">Products</a>
              <a href="#about" onClick={(e) => scrollToSection(e, 'about')} className="hover:text-amber-400 transition-colors">About</a>
              <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="hover:text-amber-400 transition-colors">Contact</a>
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, 'contact')}
                className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-2 rounded-full hover:shadow-xl hover:shadow-amber-500/50 transition-all duration-300 font-semibold inline-block"
                aria-label="Get a quote - scroll to contact section"
              >
                Get Quote
              </a>
            </div>

            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/98 backdrop-blur-lg pt-20">
          <div className="flex flex-col items-center space-y-8 p-8">
            <a href="#home" onClick={(e) => scrollToSection(e, 'home')} className="text-2xl hover:text-amber-400 transition-colors">Home</a>
            <a href="#products" onClick={(e) => scrollToSection(e, 'products')} className="text-2xl hover:text-amber-400 transition-colors">Products</a>
            <a href="#about" onClick={(e) => scrollToSection(e, 'about')} className="text-2xl hover:text-amber-400 transition-colors">About</a>
            <a href="#contact" onClick={(e) => scrollToSection(e, 'contact')} className="text-2xl hover:text-amber-400 transition-colors">Contact</a>
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, 'contact')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-3 rounded-full text-xl font-semibold inline-block"
              aria-label="Get a quote - scroll to contact section"
            >
              Get Quote
            </a>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <main id="main-content">
        <section id="home" className="relative pt-32 pb-20 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-blue-500/10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-16 animate-fadeIn">
              <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 bg-clip-text text-transparent">
                The Mark of Refined Interiors.
              </h1>
              <p className="text-xl md:text-2xl text-slate-300 mb-4">
                Transforming Spaces with Elegance & Precision
              </p>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                From premium window treatments to luxurious glass films, we craft beauty into every detail
              </p>
            </div>

            {/* Category Filter */}
            <div id="products" className="flex flex-wrap justify-center gap-4 mb-12" role="group" aria-label="Product category filters">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${activeCategory === 'all'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/50'
                  : 'bg-slate-800 hover:bg-slate-700'
                  }`}
                aria-pressed={activeCategory === 'all'}
                aria-label="Show all products"
              >
                All Products
              </button>
              {products.map((product) => (
                <button
                  key={product.category}
                  onClick={() => setActiveCategory(product.category)}
                  className={`px-6 py-2 rounded-full transition-all duration-300 ${activeCategory === product.category
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 shadow-lg shadow-amber-500/50'
                    : 'bg-slate-800 hover:bg-slate-700'
                    }`}
                  aria-pressed={activeCategory === product.category}
                  aria-label={`Filter by ${product.category}`}
                >
                  {product.category}
                </button>
              ))}
            </div>

            {/* Products Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
              {filteredProducts.map((product, idx) => (
                <div
                  key={product.category}
                  className="group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 hover:border-amber-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${product.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`}></div>

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-3xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                        {product.category}
                      </h3>
                      <div className={`w-16 h-16 bg-gradient-to-br ${product.gradient} rounded-xl opacity-80 group-hover:opacity-100 transition-opacity shadow-lg`}></div>
                    </div>

                    <div className="space-y-4">
                      {product.items.map((item, itemIdx) => (
                        <div
                          key={itemIdx}
                          className="flex items-start space-x-3 group/item cursor-pointer p-3 rounded-lg hover:bg-slate-700/30 transition-all duration-300"
                        >
                          <ChevronRight className="w-5 h-5 text-amber-400 mt-1 group-hover/item:translate-x-1 transition-transform" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg text-white group-hover/item:text-amber-400 transition-colors">
                              {item.name}
                            </h4>
                            <p className="text-sm text-slate-400 mt-1">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-slate-800/50 to-slate-900/50">
          <div className="max-w-7xl mx-auto">
            <h3 className="text-4xl md:text-5xl font-bold text-center mb-16 bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              Excellence in Every Detail
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { title: 'Premium Quality', desc: 'We source only the finest materials from trusted suppliers and deliver exceptional craftsmanship that stands the test of time. Every product undergoes rigorous quality checks to ensure lasting beauty and durability.' },
                { title: 'Custom Solutions', desc: 'Every space is unique, and so are your needs. We create tailored designs that perfectly match your vision, style preferences, and functional requirements. From concept to completion, your satisfaction is our priority.' },
                { title: 'Expert Installation', desc: 'Our skilled professionals bring years of experience and meticulous attention to detail to every project. We ensure flawless installation with minimal disruption, leaving your space transformed and pristine.' }
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700 hover:border-amber-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/20"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl mb-6 flex items-center justify-center text-3xl font-bold text-slate-900">
                    {idx + 1}
                  </div>
                  <h4 className="text-2xl font-bold mb-3">{feature.title}</h4>
                  <p className="text-slate-400">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <AboutSection />

        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* Gallery Section */}
        {/* <GallerySection /> */}

        {/* FAQ Section */}
        {/* <FAQSection /> */}

        {/* Contact Section */}
        <section id="contact" className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              Transform Your Space Today
            </h3>
            <p className="text-xl text-slate-300 mb-12">
              Let's bring elegance and luxury to your home or office
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12">
              <a
                href="tel:+919803339555"
                className="flex items-center space-x-3 bg-slate-800 px-6 py-4 rounded-full hover:bg-slate-700 transition-colors"
                aria-label="Call us at +91 980-333-9555"
              >
                <Phone className="w-5 h-5 text-amber-400" aria-hidden="true" />
                <span>+91 980-333-9555</span>
              </a>
              <a
                href="mailto:info@sbjglassworks.com"
                className="flex items-center space-x-3 bg-slate-800 px-6 py-4 rounded-full hover:bg-slate-700 transition-colors"
                aria-label="Email us at info@sbjglassworks.com"
              >
                <Mail className="w-5 h-5 text-amber-400" aria-hidden="true" />
                <span>info@sbjglassworks.com</span>
              </a>
            </div>
            <a
              href="tel:+919803339555"
              className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 px-12 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 hover:scale-105"
              aria-label="Call us to request a consultation"
            >
              Request Consultation
            </a>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12 px-6" role="contentinfo">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <span className="text-lg font-bold text-slate-900">SBJ</span>
            </div>
            <h4 className="text-xl font-bold">SBJ Glassworks</h4>
          </div>
          <p className="text-slate-400 mb-4">Shri Bala Ji Glassworks - Crafting Elegance Since Years</p>
          <p className="text-sm text-slate-500">© 2025 SBJ Glassworks. All rights reserved.</p>
          <div class="footer-credits">
            <p>Created by <a href="https://www.veronsolutions.in/" target="_blank" rel="noopener">Veron IT Solutions</a>
            </p>
        </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <WhatsAppButton />

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 1s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SBJGlassworks;